import { map, find, filter } from 'lodash-es';
import { defineStore } from 'pinia';
import useExportQueueStore from '@/stores/manuscripts/exportQueue';
import * as store from '@/lib/constants/manuscripts/store';

const useExportStore = defineStore('manuscript-export', {
  state: () => ({
    selectedExportIds: [],
  }),
  getters: {
    exportItems() {
      const exportQueueStore = useExportQueueStore();

      return map(exportQueueStore.queueItems, (item) => {
        const { export_id: exportId } = item;
        const isSelected = this.selectedExportIds.includes(exportId);
        const downloadLink = this.downloadLink(item);
        const canDownload = this.canDownload(item);

        return {
          ...item,
          isSelected,
          canDownload,
          canSelect: canDownload && (isSelected || this.canAddToSelection),
          downloadLink,
        };
      });
    },
    canAddToSelection() {
      const incrementedCount = (this.selectedExportIds?.length || 0) + 1;

      return (incrementedCount <= store.MAX_SELECTED_EXPORTS)
        && this.hasUnselectedItems;
    },
    hasUnselectedItems() {
      const exportQueueStore = useExportQueueStore();

      return !!find(exportQueueStore.queueItems, (item) => {
        const canDownload = this.canDownload(item);
        const isSelected = this.selectedExportIds.includes(item.export_id);

        return canDownload && !isSelected;
      });
    },
    canSelectAll() {
      return this.allSelected || this.canAddToSelection;
    },
    isMaxSelected() {
      return this.selectedExportIds?.length >= store.MAX_SELECTED_EXPORTS;
    },
    selectedItems() {
      return filter(this.downloadableItems, (item) => item.isSelected);
    },
    allSelected() {
      return !!this.downloadableItems?.length
        && (
          this.downloadableItems.every((item) => item.isSelected)
            || (this.isMaxSelected && !!this.selectedItems?.length)
        );
    },
    downloadableItems() {
      return filter(this.exportItems, (i) => i.canDownload);
    },
    canDownload() {
      return (queueItem) => !!this.downloadLink(queueItem);
    },
    downloadLink() {
      return (queueItem) => {
        if (queueItem?.status !== store.QUEUE_ITEM_STATUS_COMPLETE) {
          return null;
        }

        return this.downloadUrl(queueItem?.export_id);
      };
    },
    downloadUrl() {
      return (exportId) => {
        const appDomain = this.domains.appDomain.replace('/learn', '');

        return `${appDomain}/suite/api/manuscript/exports/download/${exportId}`;
      };
    },
  },
  actions: {
    [store.DOWNLOAD_SELECTED_EXPORTS]() {
      this.selectedExportIds.forEach((exportId, index) => {
        const downloadUrl = this.downloadUrl(exportId);
        const a = document.createElement('a');

        a.href = downloadUrl;

        a.style.display = 'none';

        document.body.appendChild(a);

        setTimeout(() => {
          a.click();

          setTimeout(() => {
            document.body.removeChild(a);
          }, 250);
        }, index * 1500);
      });

      this[store.CLEAR_SELECTED_EXPORTS]();
    },
    [store.CLEAR_SELECTED_EXPORTS]() {
      this.selectedExportIds = [];
    },
    [store.TOGGLE_EXPORT_ITEM](queueItem) {
      const { export_id: exportId } = queueItem;
      const index = this.selectedExportIds.findIndex((id) => id === exportId);

      if (index === -1 && this.canAddToSelection) {
        this.selectedExportIds.push(exportId);
      } else {
        this.selectedExportIds.splice(index, 1);
      }
    },
    [store.TOGGLE_ALL_EXPORTS]({ isSelected }) {
      const exportQueueStore = useExportQueueStore();

      exportQueueStore.queueItems.forEach((queueItem) => {
        const { export_id: exportId } = queueItem;
        const index = this.selectedExportIds.findIndex((id) => id === exportId);

        if (
          isSelected
          && index === -1
          && this.canDownload(queueItem)
          && this.canAddToSelection
        ) {
          this.selectedExportIds.push(exportId);
        } else if (!isSelected && index !== -1) {
          this.selectedExportIds.splice(index, 1);
        }
      });
    },
  },
});

export default useExportStore;
